'use client';

import { pathPrefixes, paths } from '@/constants/paths';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTranslations } from 'next-intl';
import { myAccountLinks } from '@/constants/myAccountLinks';
import { useIsClubMember } from '@/ui/hooks/useIsClubMember';
import useUser from '@/ui/providers/userProvider';
import { slugify } from '@/utils/url-helper';

export default function MyAccountNavigation() {
  const t = useTranslations('MyAccountNavigation'); // Assuming you use translations, otherwise remove this line
  const { localizedLink } = useLocaleLink();
  const currentPath = usePathname();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const activeLinkRef = useRef<HTMLAnchorElement | null>(null);
  const isClubMember = useIsClubMember();
  const { session } = useUser();

  // Scroll the active link into view
  useEffect(() => {
    if (activeLinkRef.current && containerRef.current) {
      const container = containerRef.current;
      const activeLink = activeLinkRef.current;
      // check if window is bigger than 640px

      const containerRect = container.getBoundingClientRect();
      const activeLinkRect = activeLink.getBoundingClientRect();

      // Calculate the position to scroll to minus the padding
      const scrollLeft =
        activeLinkRect.left -
        containerRect.left +
        container.scrollLeft -
        (window.innerWidth > 640 ? 32 : 12);

      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
  }, [activeLinkRef, containerRef, currentPath]);

  // show if current path has /my-account after second slash
  if (!session || currentPath.split('/')[2] !== pathPrefixes.myAccount) {
    return null;
  }

  function NavigationAttentionIndicator() {
    return (
      <span className="absolute -right-1 top-0 h-4 w-4 rounded-full border-2 border-white bg-red" />
    );
  }

  return (
    <nav
      ref={containerRef}
      aria-label={t('accountNavigation')}
      className="relative -z-10 mx-auto mb-5 mt-2 max-w-screen-2xl overflow-x-auto whitespace-nowrap px-3 py-1 sm:mb-7 sm:mt-4 sm:px-8 lg:mb-9"
    >
      <div className="flex gap-4">
        {myAccountLinks.map((link) => {
          const isActive = currentPath.split('/')[3] === link.href.split('/')[2];
          const clubLinkLabelRequiresRedDot = !isClubMember && link.href === paths.club;

          return (
            <Link
              key={link.href}
              href={localizedLink(link.href)}
              ref={isActive ? activeLinkRef : null}
              aria-current={isActive ? 'page' : undefined}
              className={twMerge(
                'relative block rounded-full border-0 bg-white px-5 py-3 text-center text-sm font-bold uppercase text-neutral-100 shadow-[inset_0_0_0_1px_#000] transition-[background-color,box-shadow] duration-200 ease-in hover:bg-primary-100 hover:shadow-[inset_0_0_0_2px_#FECB3F] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#406AFF]',
                isActive && 'bg-primary-100 shadow-[inset_0_0_0_1px_#000]',
              )}
              data-tile-element-id={`my-account-tab-${slugify(link.label)}`}
            >
              {t(link.label)}
              {clubLinkLabelRequiresRedDot && <NavigationAttentionIndicator />}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
